import { debounce } from 'lodash-es';
import { ContextMenuCommandRegistry } from './commandRegistry';
import { DisposableBag } from './disposableBag';
import { resolveContextMenuAnchor } from './anchorResolver';
import { resolveAnchorCorners } from './cornerAnchorResolver';
import type { TContextMenuAnchor } from './anchorTypes';
import { LatestRafScheduler } from './scheduler';
import { IContextMenu, IContextMenuManualAnchor, TContextMenuInvalidation } from './types';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { IPoint3d } from '@/editor/math/IPoint3d';
import { globalStores } from '@/editor/stores';
import { EEntityType, EContextAction, EOperateMode, ESceneMode, IEntityInfo } from '@/editor/types';
import { EAutoLayoutMode } from '@/editor/utils/layout/detect';
import { getAvailableMenuItems } from './menuPolicy';
import { FLAGS } from '@/flags';

type TContextMenuListener = () => void;

/** 开关关闭时拖拽也要走旧摆位，否则「关掉开关＝改造前的行为」这条不成立 */
function createManualAnchor(point: IPoint3d): TContextMenuAnchor {
  return FLAGS.FEATURES.CTX_MENU_POS_OPTIMIZE ? { kind: 'manual', point } : { kind: 'legacy', point };
}

function createDefaultContextMenuData(): IContextMenu {
  return {
    visible: false,
    anchor: { kind: 'legacy', point: { x: 0, y: 0, z: 0 } },
    entityInfo: {
      entityId: '',
      type: EEntityType.EMPTY
    },
    items: [],
    disabledCommonMenu: false,
    hasAlignReference: false
  };
}

export class ContextMenuController {
  private readonly _menuVisibleModes: readonly EOperateMode[] = [EOperateMode.EMPTY, EOperateMode.Alignment];
  private readonly _disposables = new DisposableBag();
  private readonly _scheduler = new LatestRafScheduler();
  private readonly _commands = new ContextMenuCommandRegistry();
  private readonly _listeners = new Set<TContextMenuListener>();
  private readonly _actionStateListeners = new Set<TContextMenuListener>();

  private _sceneMode: ESceneMode = globalStores.sceneMode;
  private _manualAnchor: IContextMenuManualAnchor | null = null;
  private _snapshot: IContextMenu = createDefaultContextMenuData();
  private _destroyed = false;

  constructor() {
    this._bindEvents();
  }

  getSnapshot = () => this._snapshot;

  subscribe = (listener: TContextMenuListener) => {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  };

  subscribeActionState = (listener: TContextMenuListener) => {
    this._actionStateListeners.add(listener);
    return () => {
      this._actionStateListeners.delete(listener);
    };
  };

  public updatePosition = (point: IPoint3d) => {
    if (this._destroyed || !this._snapshot.visible) return;

    this._invalidate({ type: 'manual-anchor', point });
  };

  public onClick = async (type: EContextAction, entityInfo: IEntityInfo) => {
    await this._commands.dispatch(type, entityInfo);
  };

  destroy() {
    this._destroyed = true;
    this._debouncedViewportRefresh.cancel();
    this._scheduler.destroy();
    this._disposables.dispose();
    this._listeners.clear();
    this._actionStateListeners.clear();
  }

  private _bindEvents() {
    this._disposables
      .add(editorEvent.on(EditorEventName.ENTITY_SELECTED, this._handleSelectionChange))
      .add(editorEvent.on(EditorEventName.HISTORY_CHANGE, this._handleHistoryChange))
      .add(editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this._handleOperateModeChange))
      .add(editorEvent.on(EditorEventName.RESIZE, this._handleResize))
      .add(editorEvent.on(EditorEventName.VIEW2D_CHANGE, this._handleViewportChange))
      .add(editorEvent.on(EditorEventName.VIEW3D_CHANGE, this._handleViewportChange))
      .add(editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this._handleSceneModeChange))
      .add(editorEvent.on(EditorEventName.ALIGN_REFERENCE_SELECTED, this._handleAlignReferenceSelected))
      .add(globalStores.editorStateStore.onChange('moving', this._handleMovingChange))
      .add(globalStores.editorStateStore.onChange('alignEnabled', this._handleAlignToggle));
  }

  private _handleSelectionChange = () => {
    const { selected } = globalStores.editorStateStore;
    const isSameEntity =
      this._snapshot.entityInfo.entityId === selected.entityId && this._snapshot.entityInfo.type === selected.type;

    if (!isSameEntity) {
      this._manualAnchor = null;
    }

    this._invalidate({ type: 'refresh', reason: 'selection', anchor: 'resolve' });
  };

  private _handleHistoryChange = () => {
    if (!this._snapshot.visible) return;
    this._manualAnchor = null;
    this._invalidate({ type: 'refresh', reason: 'history', anchor: 'resolve' });
  };

  private _handleOperateModeChange = () => {
    this._invalidate({ type: 'refresh', reason: 'operate-mode', anchor: 'keep' });
  };

  private _handleResize = () => {
    this._invalidate({
      type: 'refresh',
      reason: 'resize',
      anchor: this._sceneMode === ESceneMode.scene3d ? 'resolve' : 'keep'
    });
  };

  private _handleViewportChange = () => {
    const epoch = this._invalidate({ type: 'hide', reason: 'viewport' });
    this._debouncedViewportRefresh(epoch);
  };

  private _handleSceneModeChange = (sceneMode: ESceneMode) => {
    this._sceneMode = sceneMode;
    this._manualAnchor = null;
    this._invalidate({ type: 'refresh', reason: 'scene-mode', anchor: 'resolve' });
  };

  private _handleMovingChange = (newValue: boolean) => {
    if (newValue) {
      this._invalidate({ type: 'hide', reason: 'move' });
      return;
    }

    this._invalidate({ type: 'refresh', reason: 'move', anchor: 'resolve' });
  };

  private _handleAlignToggle = () => {
    this._invalidate({ type: 'refresh', reason: 'align', anchor: 'resolve' });
  };

  private _handleAlignReferenceSelected = () => {
    this._invalidate({ type: 'align-reference' });
  };

  private _debouncedViewportRefresh = debounce((epoch: number) => {
    if (!this._scheduler.isCurrent(epoch)) return;

    this._manualAnchor = null;
    this._invalidate({ type: 'refresh', reason: 'viewport', anchor: 'resolve' });
  }, 500);

  private _invalidate(invalidation: TContextMenuInvalidation) {
    const epoch = this._scheduler.nextEpoch();
    if (this._destroyed) return epoch;

    this._scheduler.schedule(epoch, () => {
      if (!this._scheduler.isCurrent(epoch)) return;
      this._commit(this._deriveData(invalidation));
    });
    return epoch;
  }

  private _deriveData(invalidation: TContextMenuInvalidation): IContextMenu {
    const { editorStateStore } = globalStores;
    const { selected } = editorStateStore;
    const disabledCommonMenu = editorStateStore.alignEnabled;

    if (invalidation.type === 'manual-anchor') {
      this._manualAnchor = {
        entityInfo: this._snapshot.entityInfo,
        point: invalidation.point
      };
      return {
        ...this._snapshot,
        anchor: createManualAnchor(invalidation.point),
        disabledCommonMenu,
        hasAlignReference: disabledCommonMenu ? this._snapshot.hasAlignReference : false
      };
    }

    if (invalidation.type === 'align-reference') {
      return {
        ...this._snapshot,
        disabledCommonMenu,
        hasAlignReference: disabledCommonMenu
      };
    }

    if (invalidation.type === 'hide') {
      return {
        ...this._snapshot,
        visible: false,
        disabledCommonMenu,
        hasAlignReference: disabledCommonMenu ? this._snapshot.hasAlignReference : false
      };
    }

    if (this._shouldHideForTransientState()) {
      return {
        ...this._snapshot,
        visible: false,
        disabledCommonMenu,
        hasAlignReference: disabledCommonMenu ? this._snapshot.hasAlignReference : false
      };
    }

    if (selected.type === EEntityType.EMPTY) {
      this._manualAnchor = null;
      return {
        ...createDefaultContextMenuData(),
        disabledCommonMenu
      };
    }

    const entityInfo: IEntityInfo = {
      entityId: selected.entityId,
      type: selected.type
    };

    const menuItems = getAvailableMenuItems({
      sceneMode: this._sceneMode,
      entityInfo
    });
    const shouldShow = this._menuVisibleModes.includes(globalStores.operateInfo.mode) && menuItems.length > 0;
    const nextAnchor = this._resolveAnchor(entityInfo, invalidation.anchor);

    if (!nextAnchor) {
      return {
        ...this._snapshot,
        items: menuItems,
        visible: false,
        disabledCommonMenu,
        hasAlignReference: disabledCommonMenu ? this._snapshot.hasAlignReference : false,
        entityInfo
      };
    }

    return {
      visible: shouldShow,
      anchor: nextAnchor,
      items: menuItems,
      disabledCommonMenu,
      hasAlignReference: disabledCommonMenu ? this._snapshot.hasAlignReference : false,
      entityInfo
    };
  }

  private _shouldHideForTransientState() {
    const { editorStateStore } = globalStores;
    return editorStateStore.moving || editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE;
  }

  private _getManualAnchor(owner: IEntityInfo) {
    if (!this._manualAnchor) return;

    const isSameOwner =
      this._manualAnchor.entityInfo.entityId === owner.entityId && this._manualAnchor.entityInfo.type === owner.type;

    return isSameOwner ? this._manualAnchor.point : undefined;
  }

  private _resolveAnchor(owner: IEntityInfo, anchorMode: 'keep' | 'resolve'): TContextMenuAnchor | undefined {
    const manual = this._getManualAnchor(owner);
    if (manual) return createManualAnchor(manual);

    if (
      anchorMode === 'keep' &&
      this._snapshot.entityInfo.entityId === owner.entityId &&
      this._snapshot.entityInfo.type === owner.type
    ) {
      return this._snapshot.anchor;
    }

    const params = { sceneMode: this._sceneMode, entityInfo: owner };
    if (FLAGS.FEATURES.CTX_MENU_POS_OPTIMIZE) {
      const corners = resolveAnchorCorners(params);
      if (corners.length) return { kind: 'corners', corners };
    }

    // 开关期的兜底：数据缺失时退回旧 resolver，随 flag 一起删
    const point = resolveContextMenuAnchor(params);
    return point ? { kind: 'legacy', point } : undefined;
  }

  private _commit(data: IContextMenu) {
    if (this._destroyed) return;

    this._snapshot = data;
    this._listeners.forEach((listener) => listener());
  }
}
